import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { ViewScoreSmall } from "../../../../../coding/views/ViewScoreSmall";
import { LifeCycleStageControls } from "./LifeCycleStageControls";
import { DesignStageControls } from "./DesignStageControls";
import { UploadNotFinishedAlert } from "./UploadNotFinishedAlert";
import { CreateOrUpdateCraWayModal } from "../../../../../cra/views/CreateOrUpdateRoute/CreateOrUpdateCraWayModal";
import { DeleteCraWayModal } from "../../../../../cra/views/DeleteCraWayModal/DeleteCraWayModal";
import { SelectFirstNodeThumbnail } from "./SelectFirstNodeThumbnail";
import { CraWayStats } from "./CraWayStats";
import { LifeCycleStage } from "../../../../../cra/models/LifeCycleStage";
import { useStores } from "../../../../../shared/stores/RootStore";
import {
  ArrowBackIcon,
  DeleteIcon,
  EditIcon,
} from "../../../../../shared/views/icons";
import { PopulateWayControls } from "./PopulateWayControls";

const i18prefix = "map:routeSummary";

const CraWayDetailBase: React.FC = () => {
  const { mapStore, authStore, craNodeStore, craWayStore } = useStores();
  const wayEditingModal = useDisclosure();
  const wayDeletingModal = useDisclosure();

  const craWay = craWayStore.getSelected()!;
  const nodes = craNodeStore.nodesByWayId.get(craWay.id);
  const firstNode = nodes?.[0];

  const refreshCraWay = async () => {
    Promise.all([
      craWayStore.refreshCraWay(craWay.id),
      craNodeStore.fetchNodes(craWay.id),
    ]);
  };

  const selectUnprocessedNodes = () => {
    const multiSelectedIds = new Set<string>();
    nodes?.forEach((node) => {
      if (node.photo === null) {
        multiSelectedIds.add(node.id);
      }
    });
    craNodeStore.setMultiSelectedIds(multiSelectedIds);
  };

  return (
    <Stack py={2} gap={4} overflowY={"auto"}>
      <HStack px={2} mb={-2}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={t(`${i18prefix}.clearSelection`)}
            onClick={() => {
              craWayStore.clearSelected();
              mapStore.recenterMap();
            }}
            title={t(`${i18prefix}.clearSelection`)}
          >
            <Icon as={ArrowBackIcon} />
          </IconButton>
        </Box>
        <Text flexGrow={1} px={2} fontWeight="semibold" textAlign="center">
          {craWay.name}
        </Text>
        <Box flexShrink={0} width="40px" />
      </HStack>

      {authStore.isAuthenticated && (
        <ButtonGroup
          alignSelf="center"
          colorPalette="primary"
          variant="outline"
          size="sm"
          attached
        >
          <IconButton
            aria-label={t(`${i18prefix}.edit`)}
            title={t(`${i18prefix}.edit`)}
            disabled={!craWay.permissions.canUpdate}
            onClick={wayEditingModal.onOpen}
          >
            <EditIcon />
          </IconButton>

          <IconButton
            aria-label={t(`${i18prefix}.delete`)}
            title={t(`${i18prefix}.delete`)}
            disabled={!craWay.permissions.canDelete}
            onClick={wayDeletingModal.onOpen}
          >
            <DeleteIcon />
          </IconButton>
        </ButtonGroup>
      )}

      {authStore.isAuthenticated && (
        <LifeCycleStageControls craWay={craWay} size="sm" />
      )}

      <Stack px={4} gap={4}>
        <CraWayStats />

        {craWay.lifeCycleStage === LifeCycleStage.Design &&
          craWay.permissions.canUpdate && <DesignStageControls />}

        {craWay.score && (
          <HStack py={2} justifyContent={"center"}>
            <ViewScoreSmall score={craWay.score} />
          </HStack>
        )}

        {craWay.lifeCycleStage === LifeCycleStage.DataCollection &&
          !craWay.uploadFinished && (
            <UploadNotFinishedAlert craWayId={craWay.id}>
              <Button size="sm" colorPalette="primary" onClick={refreshCraWay}>
                {t("craway:photos.refresh")}
              </Button>
              <Button
                size="sm"
                colorPalette="primary"
                onClick={selectUnprocessedNodes}
              >
                {t("craway:photos.selectUnprocessedNodes")}
              </Button>
            </UploadNotFinishedAlert>
          )}

        {craWay.lifeCycleStage === LifeCycleStage.DataCollection &&
          !nodes?.length && <PopulateWayControls />}

        {craWay.lifeCycleStage !== LifeCycleStage.Coding &&
          craWay.lifeCycleStage !== LifeCycleStage.Design &&
          !!firstNode?.photo && (
            <SelectFirstNodeThumbnail
              firstNode={firstNode}
              selectNodeById={(nodeId) => {
                craNodeStore.select(nodeId);
                mapStore.recenterMap();
              }}
            />
          )}
      </Stack>

      {wayEditingModal.open && (
        <CreateOrUpdateCraWayModal
          onClose={wayEditingModal.onClose}
          craWay={craWay}
        />
      )}

      {wayDeletingModal.open && (
        <DeleteCraWayModal onClose={wayDeletingModal.onClose} />
      )}
    </Stack>
  );
};

export const CraWayDetail = observer(CraWayDetailBase);
